import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, CanActivateFn, CanActivateChildFn, CanMatchFn } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '@app/core/auth/auth.service';
import { switchMap } from 'rxjs/operators';

/**
 * Check the authenticated status
 *
 * @param redirectURL
 */
function checkAuth(redirectURL: string): Observable<boolean>
{
    const authService = inject(AuthService);
    const router = inject(Router);

    // Check the authentication status
    return authService.check()
               .pipe(
                   switchMap((authenticated) => {

                       // If the user is not authenticated...
                       if ( !authenticated )
                       {
                           // Redirect to the sign-in page
                           router.navigate(['sign-in'], {queryParams: {redirectURL}});

                           // Prevent the access
                           return of(false);
                       }

                       if (authService.user?.must_change_password && redirectURL !== '/change-password-required' && redirectURL !== '/sign-out') {
                           router.navigateByUrl('/change-password-required');
                           return of(false);
                       }
                       if (authService.isOperator && !redirectURL.startsWith('/operator') && redirectURL !== '/sign-out') {
                           router.navigateByUrl('/operator');
                           return of(false);
                       }
                       // Allow the access
                       return of(true);
                   })
               );
}

/**
 * Can activate functional guard
 */
export const canActivateAuth: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const redirectUrl = state.url;
    return checkAuth(redirectUrl);
};

/**
 * Can activate child functional guard
 */
export const canActivateChildAuth: CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const redirectUrl = state.url;
    return checkAuth(redirectUrl);
};

/**
 * Can match functional guard
 */
export const canMatchAuth: CanMatchFn = (route, segments) => {
    return checkAuth('/');
};

// Legacy class-based guard (deprecated - use functional guards above)
// @deprecated Use canActivateAuth, canActivateChildAuth, canMatchAuth instead
export { canActivateAuth as AuthGuard }
